import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { siteBackgroundFor } from "../../config/siteBackground";
import "./SiteBackground.css";

/* The site-wide dot-wave background (2026-10-10). One fixed layer behind
   every page, switched per route by src/config/siteBackground.js
   (full / faint / off). It replaces the homepage-only layer from PR #37 and
   the faint grid behind sign-in and sign-up.

   One file, one URL: every page uses the same SVG, so it is downloaded once
   and then served from the browser cache.

   The image is requested only after the page has finished loading and the
   browser is idle, so it never competes with the content of the page. On a
   page whose setting is "off" nothing is requested at all; the wait starts
   the first time a page wants the background. */
function useDeferredStart(wanted) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready || !wanted) return undefined;
    let idle = 0;
    let timer = 0;
    const start = () => {
      if (typeof window.requestIdleCallback === "function") {
        idle = window.requestIdleCallback(() => setReady(true), { timeout: 2000 });
      } else {
        timer = window.setTimeout(() => setReady(true), 200);
      }
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      if (idle && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idle);
      window.clearTimeout(timer);
    };
  }, [ready, wanted]);
  return ready;
}

export function useSiteBackgroundMode() {
  const { pathname, search } = useLocation();
  return siteBackgroundFor(pathname, search);
}

/* The fixed layer. Rendered first in SiteShell. */
export default function SiteBackground() {
  const mode = useSiteBackgroundMode();
  const ready = useDeferredStart(mode !== "off");

  // Page styles key off this attribute: where the background shows, page
  // wrappers drop their solid fill so it can show through, and translucent
  // cards are laid over a solid base (SiteBackground.css).
  useEffect(() => {
    document.documentElement.setAttribute("data-site-bg", mode);
  }, [mode]);
  useEffect(() => () => document.documentElement.removeAttribute("data-site-bg"), []);

  if (mode === "off") return null;
  return (
    <div
      className={`site-bg site-bg--${mode}${ready ? " site-bg--on" : ""}`}
      aria-hidden="true"
      data-testid="site-bg"
    />
  );
}

/* A short, eased fade just before the footer, so the dots dissolve rather
   than stop and the footer's own contour pattern never sits next to them.
   Rendered between the page and the footer in SiteShell. */
export function SiteBackgroundFade() {
  const mode = useSiteBackgroundMode();
  if (mode === "off") return null;
  return (
    <div className="site-bg-fade-anchor" aria-hidden="true">
      <div className="site-bg-fade" data-testid="site-bg-fade" />
    </div>
  );
}
